<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App'
}
</script>

<style>
/* ── Design tokens ───────────────────────────────────────── */
:root {
  --mob-orange:        rgb(255, 152, 0);
  --mob-orange-dark:   rgb(230, 126, 0);
  --mob-orange-light:  rgba(255, 152, 0, 0.12);
  --mob-dark:          #212121;
  --mob-dark-2:        #2a2a2a;
  --mob-dark-3:        #333333;
  --mob-surface:       #ffffff;
  --mob-bg:            #f5f5f5;
  --mob-text:          #212121;
  --mob-text-muted:    #757575;
  --mob-border:        #e0e0e0;
  --mob-radius:        10px;
  --mob-shadow:        0 2px 12px rgba(0,0,0,0.08);
  --mob-shadow-md:     0 4px 24px rgba(0,0,0,0.12);
}

/* ── Reset ───────────────────────────────────────────────── */
*, *::before, *::after { box-sizing: border-box; }

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: var(--mob-bg);
  color: var(--mob-text);
  margin: 0;
  -webkit-font-smoothing: antialiased;
}

#app { min-height: 100vh; }

/* ── Bootstrap overrides ─────────────────────────────────── */
.btn-primary {
  background-color: var(--mob-orange) !important;
  border-color: var(--mob-orange) !important;
  color: #fff !important;
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 8px !important;
  transition: background 0.18s, box-shadow 0.18s !important;
}
.btn-primary:hover, .btn-primary:focus {
  background-color: var(--mob-orange-dark) !important;
  border-color: var(--mob-orange-dark) !important;
  box-shadow: 0 4px 14px rgba(255,152,0,0.35) !important;
}
.btn-outline-primary {
  color: var(--mob-orange) !important;
  border-color: var(--mob-orange) !important;
  border-radius: 8px !important;
  font-weight: 600;
}
.btn-outline-primary:hover {
  background-color: var(--mob-orange) !important;
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(255,152,0,0.25) !important;
}
.btn-outline-secondary { border-radius: 8px !important; }
.btn-outline-success  { border-radius: 8px !important; }
.btn-outline-danger   { border-radius: 8px !important; }
.btn-outline-warning  { border-radius: 8px !important; }
.btn-outline-info     { border-radius: 8px !important; }

.btn { border-radius: 8px !important; }

.card {
  border: none !important;
  border-radius: var(--mob-radius) !important;
  box-shadow: var(--mob-shadow) !important;
}
.card-header {
  border-radius: var(--mob-radius) var(--mob-radius) 0 0 !important;
  border-bottom: 1px solid var(--mob-border) !important;
  background: #fff !important;
  font-weight: 600;
}
.card-footer {
  border-top: 1px solid var(--mob-border) !important;
  background: #fafafa !important;
  border-radius: 0 0 var(--mob-radius) var(--mob-radius) !important;
}

.form-control {
  border-radius: 8px !important;
  border: 1.5px solid var(--mob-border) !important;
  font-size: 0.9rem !important;
  transition: border-color 0.15s, box-shadow 0.15s !important;
}
.form-control:focus {
  border-color: var(--mob-orange) !important;
  box-shadow: 0 0 0 3px rgba(255,152,0,0.15) !important;
}

.badge { border-radius: 6px !important; font-weight: 600; letter-spacing: 0.3px; }
.badge-primary   { background: var(--mob-orange) !important; color: #fff !important; }
.badge-warning   { background: #fff3e0 !important; color: #e65100 !important; border: 1px solid #ffe0b2 !important; }
.badge-success   { background: #e8f5e9 !important; color: #2e7d32 !important; border: 1px solid #c8e6c9 !important; }
.badge-danger    { background: #fce4ec !important; color: #c62828 !important; border: 1px solid #f8bbd0 !important; }
.badge-secondary { background: #f5f5f5 !important; color: #616161 !important; border: 1px solid #e0e0e0 !important; }
.badge-light     { background: #fafafa !important; color: #424242 !important; border: 1px solid #e0e0e0 !important; }
.badge-info      { background: #e3f2fd !important; color: #1565c0 !important; border: 1px solid #bbdefb !important; }

.alert-danger  { border-radius: 8px !important; border-left: 4px solid #ef5350 !important; background: #fce4ec !important; color: #b71c1c !important; border-top: none !important; border-right: none !important; border-bottom: none !important; }
.alert-success { border-radius: 8px !important; border-left: 4px solid #66bb6a !important; background: #e8f5e9 !important; color: #1b5e20 !important; border-top: none !important; border-right: none !important; border-bottom: none !important; }

.table { font-size: 0.875rem; }
.table th { font-weight: 700; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.8px; color: var(--mob-text-muted); border-bottom: 2px solid var(--mob-border) !important; }
.table td { vertical-align: middle !important; border-color: var(--mob-border) !important; }
.thead-light th { background: #fafafa !important; }
.table-hover tbody tr:hover { background: rgba(255,152,0,0.04) !important; }

/* ── Scrollbar ───────────────────────────────────────────── */
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #d0d0d0; border-radius: 99px; }
::-webkit-scrollbar-thumb:hover { background: #bdbdbd; }

/* ── Utilities ───────────────────────────────────────────── */
.min-vh-100 { min-height: 100vh; }
.font-weight-semibold { font-weight: 600; }

/* ── Modal overlay shared ────────────────────────────────── */
.mob-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050;
  animation: overlayIn 0.15s ease;
}
@keyframes overlayIn { from { opacity: 0 } to { opacity: 1 } }
.mob-modal-box {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  width: 100%;
  animation: slideUp 0.2s ease;
  overflow: hidden;
}
@keyframes slideUp { from { transform: translateY(16px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
.mob-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px 16px;
  border-bottom: 1px solid var(--mob-border);
}
.mob-modal-title { font-weight: 700; font-size: 1rem; margin: 0; }
.mob-modal-close {
  background: none; border: none; cursor: pointer;
  width: 30px; height: 30px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #9e9e9e; font-size: 1.2rem; transition: background 0.15s;
}
.mob-modal-close:hover { background: #f5f5f5; color: #212121; }
.mob-modal-body { padding: 22px; }

/* ── Page header ─────────────────────────────────────────── */
.mob-page-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 24px;
}
.mob-page-title { font-size: 1.25rem; font-weight: 700; margin: 0; }
</style>
